/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import type { ExtensionApi } from '../core/extension';
import {
  type Option as BaseStandardChartOption,
  type EChartsModel,
  type EChartsAPI,
  filterInsightSeries
} from '../helper/standardChart';
import { getApiByChartDom } from '../core/MainStandardChartView';

const storage = new Map<string, (context: ExtensionApi) => void>();

export interface Insight<D = unknown[]> {
  type: string;
  description: string;
  data: D;
  animationDuration?: number;
  /** 联动图例（同步弱化） */
  linkLegend?: boolean;
}

interface InsightMetaInfo<I extends Insight> {
  type: I['type'];
}

export function initInsights(context: ExtensionApi) {
  storage.forEach(fn => fn(context));
}

type Context<I extends Insight> = ExtensionApi & { insightOption: I };

function buildContext<I extends Insight>(
  baseContext: ExtensionApi,
  option: { dvInsight?: I[] },
  insightMetaInfo: InsightMetaInfo<I>
): Context<I>[] {
  return (option?.dvInsight || [])
    .filter(item => item.type === insightMetaInfo.type)
    .map(item => {
      const commonThemeOption = baseContext.token?.dvStandardChart?.baseOption?.dvInsight?.find(
        i => typeof i.type === 'undefined'
      );
      const themeOption = baseContext.token?.dvStandardChart?.baseOption?.dvInsight?.find(
        i => i.type === item.type
      );

      return {
        ...baseContext,
        insightOption: {
          ...item,
          // * 合并主题配置
          data: item.data.map(dataItem =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            baseContext.util.data.deepMerge(
              baseContext.util.data.deepMerge(dataItem, themeOption),
              commonThemeOption
            )
          )
        }
      };
    });
}

// * -------------------------------------------------------------------------------------
const echartsModule = ThsDataVStandardChart?.echarts;

export type StandardChartOption<I extends Insight = Insight> = BaseStandardChartOption & {
  /** 数据洞察 */
  dvInsight?: I[];
};

interface StandardChartInsightMetaInfo<I extends Insight = Insight> extends InsightMetaInfo<I> {
  /** 定义何时生效 */
  filter: (option: StandardChartOption) => boolean;
  /** 清理逻辑，在遇到动态图表或者多次渲染的情况下，要确保每一次重新渲染前执行清理逻辑 */
  clear: {
    /**
     * 每一次 `play()` 前执行
     */
    onOptionUpdate?: (option: StandardChartOption) => void;
    /**
     * 每一次渲染前
     * @see https://datav.iwencai.com/components/paradigm-chart/docs/docs/ecahrts-reference/api/echarts#registerupdatelifecycle
     */
    onSeriesBeforeUpdate?: (ecModel: EChartsModel, ecApi: EChartsAPI) => void;
  };
  /**
   * 状态管理
   */
  // statesProxy: {

  // },
  /** 渲染逻辑 */
  render: {
    /**
     * 每一次 `play()` 前执行
     */
    onOptionUpdate?: (
      context: Context<I>,
      option: StandardChartOption,
      legendIns?: InstanceType<ExtensionApi['ui']['Legend']['Legend']>
    ) => void;
    /**
     * 每一次渲染后
     * @see https://datav.iwencai.com/components/paradigm-chart/docs/docs/ecahrts-reference/api/echarts#registerupdatelifecycle
     */
    onSeriesAfterUpdate?: (context: Context<I>, ecModel: EChartsModel, ecApi: EChartsAPI) => void;
  };
}

export function registerStandardChartInsight<I extends Insight>(
  info: StandardChartInsightMetaInfo<I>
) {
  if (storage.has(info.type)) {
    throw new Error(`An insight with type="${info.type}" already exists.`);
  }

  function skipRender(option: StandardChartOption) {
    const insightOption = (option?.dvInsight || []).find(
      item => item.type === info.type && typeof item.data !== 'undefined'
    );
    if (typeof insightOption === 'undefined') {
      return true;
    }
    return !info.filter(filterInsightSeries(option));
  }

  storage.set(info.type, context => {
    context.lifecycle.on('standardchart:play', (chartIns, legendIns, { option = {} }) => {
      info.clear?.onOptionUpdate?.(option as StandardChartOption);

      if (!skipRender(option as StandardChartOption)) {
        const contexts = buildContext<I>(context, option as StandardChartOption<I>, info);
        contexts.forEach(ctx => {
          info.render?.onOptionUpdate?.(ctx, option as StandardChartOption, legendIns);
        });
      }
    });

    return info;
  });

  echartsModule?.use?.(extensionRegisters => {
    extensionRegisters.registerUpdateLifecycle(
      'series:beforeupdate',
      (ecModal: EChartsModel, ecApi: EChartsAPI) => {
        info.clear?.onSeriesBeforeUpdate?.(ecModal, ecApi);
      }
    );

    extensionRegisters.registerUpdateLifecycle(
      'series:afterupdate',
      (ecModal: EChartsModel, ecApi: EChartsAPI) => {
        const option = ecModal.getOption();

        if (!skipRender(option)) {
          const contexts = buildContext<I>(
            getApiByChartDom(ecApi.getDom())!,
            option as StandardChartOption<I>,
            info
          );

          contexts.forEach(ctx => {
            info.render?.onSeriesAfterUpdate?.(ctx, ecModal, ecApi);
          });
        }
      }
    );
  });
}
